<template>
  <div ref="appendixRootRef" class="appendix-section">
    <AntTabs v-model:activeKey="activeAppendixTab">
      <AntTabPane key="Description" tab="Description">
        <AntTabs
          v-if="selectedSectionState.tag === 'appendix'"
          v-model:value="activeAppendixDescType"
          type="card"
        >
          <AntTabPane key="General" tab="General">
            <div v-if="isDev">customizedVersionSelectedDocId: {{ customizedVersionSelectedDocId }}</div>
            <AntInputGroup compact style="margin: 8px 0 12px;">
              <AntInput
                class="prefix-input ant-col-3"
                placeholder="Switch Customized Version"
                defaultValue="Switch Customized Version"
                readonly
              />
              <CustomizedVersionSelect
                class="ant-col-21"
                :selectedDocId="customizedVersionSelectedDocId"
                :docsDataByDocId="docsDataByDocId"
                :selectedSectionState="selectedSectionState"
                :selectedSectionOrFieldState="selectedSectionState"
                @update:selectedDocId="val => customizedVersionSelectedDocId = val === 'general' ? undefined : val"
              />
            </AntInputGroup>

            <SectionEditor
              height="700px"
              :previewMode="false"
              v-show="activeAppendixDescType === 'General'"
              :imageManagementData="imageManagementData"
              :sectionsDataByDocId="sectionsDataByDocId"
              :customizedVersionSelectedDocId="customizedVersionSelectedDocId || `${selectedSectionState.wallet}#master`"
              :editorContent="descriptionEditorContent"
              :onChange="val => onChangeContent(val)"
            />
          </AntTabPane>
          <AntTabPane key="Restricted" tab="Restricted">
            <AntFormItem name="platforms" label="Platforms">
              <PlatformDocSelect
                type="platforms"
                :value="selectedSectionState.restricted?.platforms"
                :options="platformOptions"
                :onChange="val => emit('change', 'restricted.platforms', val)"
              />
            </AntFormItem>
            <SectionEditor
              height="700px"
              :previewMode="false"
              :imageManagementData="imageManagementData"
              :sectionsDataByDocId="sectionsDataByDocId"
              :customizedVersionSelectedDocId="`${selectedSectionState.wallet}#master`"
              :editorContent="selectedSectionState.restricted?.content"
              :onChange="val => emit('change', 'restricted.content', val)"
            />
          </AntTabPane>
        </AntTabs>

        <template v-else>
          <div v-if="isDev">customizedVersionSelectedDocId: {{ customizedVersionSelectedDocId }}</div>
          <AntInputGroup compact style="margin: 8px 0 12px;">
            <AntInput
              class="prefix-input ant-col-3"
              placeholder="Switch Customized Version"
              defaultValue="Switch Customized Version"
              readonly
            />
            <CustomizedVersionSelect
              class="ant-col-21"
              :selectedDocId="customizedVersionSelectedDocId"
              :docsDataByDocId="docsDataByDocId"
              :selectedSectionState="selectedSectionState"
              :selectedSectionOrFieldState="selectedSectionState"
              @update:selectedDocId="val => customizedVersionSelectedDocId = val === 'general' ? undefined : val"
            />
          </AntInputGroup>
          <SectionEditor
            height="700px"
            :previewMode="false"
            :imageManagementData="imageManagementData"
            :sectionsDataByDocId="sectionsDataByDocId"
            :customizedVersionSelectedDocId="customizedVersionSelectedDocId || `${selectedSectionState.wallet}#master`"
            :editorContent="descriptionEditorContent"
            :onChange="val => onChangeContent(val)"
          />
        </template>
      </AntTabPane>

      <AntTabPane
        v-if="selectedSectionState.tag && selectedSectionState.tag !== 'appendix'"
        key="Data Table"
        tab="Data Table"
      >
        <template v-if="filteredPlatformsOrder?.length">
          <div v-if="isDev">
            <div>activePlatformIdxes: {{ activePlatformIdxes }}</div>
            <div>filteredPlatformsOrder: {{ filteredPlatformsOrder }}</div>
            <div>selectedSectionState.tablePlatformsOrder: {{ selectedSectionState.tablePlatformsOrder }}</div>
          </div>

          <div style="display: flex; justify-content: flex-end; margin-bottom: 16px;">
            <AntButton type="primary" @click="onAddPlatform">
              <AddIcon /> Add Platform
            </AntButton>
          </div>

          <AntCollapse v-model:activeKey="activePlatformIdxes">
            <AntCollapsePanel
              v-for="(platform, idx) in filteredPlatformsOrder"
              :key="idx"
              class="platform-collapse-item"
              :data-platform-idx="idx"
            >
              <template #header>
                <div
                  class="platform-collapse-header"
                  @dragover.prevent
                  @dragenter="onPlatformHeaderDragenter"
                  @drop="e => onPlatformHeaderDrop(e, idx)"
                >
                  <span
                    class="platform-drag-handle"
                    draggable="true"
                    @mousedown.stop
                    @click.stop
                    @dragstart="e => onPlatformDragstart(e, idx)"
                  >
                    <DragIcon style="cursor: move; color: #999" />
                  </span>
                  <AntSelect
                    :value="platform"
                    :options="platformOptions"
                    :open="platformSelectOpenIdx === idx"
                    placeholder="Select Platform"
                    style="width: 50%;"
                    showSearch
                    @click.stop
                    @dropdownVisibleChange="open => onPlatformSelectDropdownChange(open, idx)"
                    @change="val => onChangeTableDataPlatform(idx, val)"
                  />
                </div>
              </template>
              <template #extra>
                <AntPopconfirm
                  title="Sure to delete?"
                  placement="left"
                  @confirm="onDeletePlatform(idx)"
                >
                  <DeleteIcon title="Delete" @click.stop />
                </AntPopconfirm>
              </template>
              <div v-if="platform">
                <TableDataTable
                  :ref="el => setPlatformTableRef(platform, el)"
                  :key="`${selectedSectionState.wallet}-${selectedSectionState.sectionId}-${platform}`"
                  :platform="platform"
                  :isTableLoading="isTableLoading"
                  :imageManagementData="imageManagementData"
                  :sectionsDataByDocId="sectionsDataByDocId"
                  :customizedVersionSelectedDocId="customizedVersionSelectedDocId || `${selectedSectionState.wallet}#master`"
                  :docsDataByDocId="docsDataByDocId"
                  :selectedSectionState="selectedSectionState"
                  :selectedFieldState="selectedFieldState"
                  v-bind="getTableDataTableProps()"
                />
              </div>
            </AntCollapsePanel>
          </AntCollapse>
        </template>

        <div v-else>
          <TableDataTable
            ref="tableDataTableRef"
            :key="`${selectedSectionState.wallet}-${selectedSectionState.sectionId}`"
            :isTableLoading="isTableLoading"
            :imageManagementData="imageManagementData"
            :sectionsDataByDocId="sectionsDataByDocId"
            :customizedVersionSelectedDocId="customizedVersionSelectedDocId || `${selectedSectionState.wallet}#master`"
            :docsDataByDocId="docsDataByDocId"
            :selectedSectionState="selectedSectionState"
            :selectedFieldState="selectedFieldState"
            v-bind="getTableDataTableProps()"
          />
        </div>
      </AntTabPane>
    </AntTabs>
  </div>
</template>

<script>
import {
  ref,
  watch,
  inject,
  reactive,
  computed,
  nextTick,
} from 'vue'

import { cloneDeep } from 'lodash'

import {
  Form as AntForm,
  Tabs as AntTabs,
  Button as AntButton,
  Select as AntSelect,
  Input as AntInput,
  Collapse as AntCollapse,
  Popconfirm as AntPopconfirm,
  message as antMessage,
} from 'ant-design-vue'

import AddIcon from '@ant-design/icons-vue/lib/icons/PlusOutlined'
import DeleteIcon from '@ant-design/icons-vue/lib/icons/DeleteOutlined'
import DragIcon from '@ant-design/icons-vue/lib/icons/HolderOutlined'

import TableDataTable from '@/components/ApiDoc/editor/TableDataTable.vue'
import SectionEditor from '@/components/ApiDoc/editor/SectionEditor.vue'
import PlatformDocSelect from '@/components/ApiDoc/shared/PlatformDocSelect.vue'
import CustomizedVersionSelect from '@/components/ApiDoc/editor/CustomizedVersionSelect.vue'

import { GAMETYPE_OPTIONS } from '@/constants'
import { emptyObject } from '@/utils'
import { isRootParentSectionId } from '../lib/constants'

export default {
  name: 'AppendixSection',
  components: {
    SectionEditor,
    TableDataTable,
    PlatformDocSelect,
    CustomizedVersionSelect,
    AntTabs,
    AntTabPane: AntTabs.TabPane,
    AntFormItem: AntForm.Item,
    AntInput,
    AntInputGroup: AntInput.Group,
    AntButton,
    AntSelect,
    AntCollapse,
    AntCollapsePanel: AntCollapse.Panel,
    AntPopconfirm,
    AddIcon,
    DeleteIcon,
    DragIcon,
  },
  props: {
    platformOptions: Array,
    imageManagementData: Array,
    docsDataByDocId: Object,
    sectionsBySectionId: Object,
    sectionsDataByDocId: Object,
    selectedSectionState: Object,
    selectedFieldState: Object,
  },
  emits: ['change'],
  setup(props, { emit, expose }) {
    const isDev = inject('isDev')
    const appendixRootRef = ref(null)
    const tableDataTableRef = ref(null)
    const tableDataTableRefsByPlatform = reactive({})

    const setPlatformTableRef = (platform, el) => {
      if (!platform) return
      if (el) tableDataTableRefsByPlatform[platform] = el
      else delete tableDataTableRefsByPlatform[platform]
    }

    const activeAppendixTab = ref(props.selectedSectionState.tag !== 'appendix' ? 'Data Table' : 'Description')

    watch(() => props.selectedSectionState.tag, (newVal) => {
      activeAppendixTab.value = newVal !== 'appendix' ? 'Data Table' : 'Description'
    })

    const activeAppendixDescType = ref('General')
    const isTableLoading = ref(false)

    const selectedSectionState = reactive(props.selectedSectionState)
    const selectedFieldState = reactive(props.selectedFieldState)

    const customizedVersionSelectedDocId = ref(undefined)

    const descriptionEditorContent = computed(() => {
      const docId = customizedVersionSelectedDocId.value
      if (!docId) return selectedSectionState.content
      const cv = selectedSectionState.customizedVersion?.[docId]
      if (cv != null && Object.prototype.hasOwnProperty.call(cv, 'content')) {
        return cv.content
      }
      return undefined
    })

    // 點擊 Cancel 按鈕，selectedDocId 會變回 general
    watch(() => Object.keys(selectedSectionState.customizedVersion || {}), (newVal, oldVal) => {
      console.log('[AppendixSection] Object.keys(selectedSectionState.customizedVersion)', newVal, oldVal)
      if (!newVal.includes(customizedVersionSelectedDocId.value)) customizedVersionSelectedDocId.value = undefined
    })

    // watch(() => props.selectedSectionState, (newVal, oldVal) => {
    //   console.log('[AppendixSection] watch selectedSectionState', newVal, oldVal)
    //   emptyObject(selectedSectionState)
    //   Object.assign(selectedSectionState, cloneDeep(newVal))
    // }, { deep: true, immediate: true })

    watch(
      [
        // () => props.rerenderChildTable,
        () => selectedSectionState.wallet,
        () => selectedSectionState.docId,
        () => selectedSectionState.sectionId,
      ],
      async ([newWallet, newDocId, newSectionId], [oldWallet, oldDocId, oldSectionId]) => {
        console.log('[AppendixSection] watch', {
          newWallet, oldWallet, newDocId, oldDocId, newSectionId, oldSectionId,
        })

        if (props.selectedSectionState.sectionType !== 'appendix'
          || (newWallet === oldWallet && newDocId === oldDocId && newSectionId === oldSectionId)
          || !newDocId || !newWallet || !newSectionId) return

        const clonedSelectedSectionState = cloneDeep(props.selectedSectionState) // 一定要先 clone 避免被 emptyObject 清空
        emptyObject(selectedSectionState)
        Object.assign(selectedSectionState, clonedSelectedSectionState)

        const clonedSelectedFieldState = cloneDeep(props.selectedFieldState) // 一定要先 clone 避免被 emptyObject 清空
        emptyObject(selectedFieldState)
        Object.assign(selectedFieldState, clonedSelectedFieldState)

        // const { sectionId } = selectedSectionState
        // const clonedFields = cloneDeep(props.fieldsBySectionId?.[sectionId] || {})
        // emptyObject(selectedFieldState)
        // Object.assign(selectedFieldState, clonedFields)
      },
      { immediate: true },
    )

    const platformOptions = computed(() => {
      console.log('[AppendixSection] computed platformOptions')
      if (selectedSectionState.platforms) {
        const platformOptionsById = props.platformOptions.reduce((acc, option) => {
          acc[option.value] = option
          return acc
        }, {})
        return selectedSectionState.platforms.map(id => platformOptionsById[id])
      }
      if (selectedSectionState.tag === 'platforms-regional-ip-restriction') {
        return [
          { value: '{{brand}}', label: '{{brand}}' },
          ...props.platformOptions,
        ]
      }
      return props.platformOptions
    })

    // ["HOTROAD_YYvgmMJDoR", "HRG_FjR4m8lBXA", "SEXYBCRT_NJOR5XEJ9Y"] => ["HOTROAD_YYvgmMJDoR", "SEXYBCRT_NJOR5XEJ9Y"] (HRG_FjR4m8lBXA 是 Excluded Platforms)
    //  p == null 為按下 Add Platform 按鈕新增的
    const filteredPlatformsOrder = computed(() => (selectedSectionState.tablePlatformsOrder || []).filter(p => p == null || platformOptions.value.some(({ value }) => p === value)))

    const getTableDataTableProps = () => {
      const { wallet, tag, tableColumns } = selectedSectionState
      if (!tableColumns) return {}

      const sortedColumns = Object.keys(tableColumns).sort((a, b) => tableColumns[a].sortOrder - tableColumns[b].sortOrder).map(col => tableColumns[col])
      console.log('[AppendixSection] sortedColumns', sortedColumns)
      if (tag === 'list-of-platforms') {
        return {
          columns: sortedColumns.map(({
            columnId, columnTitle, sortOrder, ...column
          }) => ({
            title: columnTitle,
            dataIndex: columnId,
            key: columnId,
            ...column,
            ...(sortOrder === 0 && { fixed: 'left' }),
            ...(columnId === 'platform' && {
              width: 200,
              ellipsis: true,
              options: platformOptions.value,
              showSearch: true,
              required: true,
            }),
            ...(columnId === 'column_1' && { // Game Types
              width: 200,
              options: GAMETYPE_OPTIONS,
              mode: 'multiple',
              showSearch: true,
              sortValue: true,
              required: true,
            }),
            ...(columnId === 'column_2' && { // Has Bet Limit Setting
              width: 200,
              mode: 'multiple',
            }),
            ...(columnId === 'column_3' && { // Bet Limit Setting
              width: 300,
            }),
          })),
        }
      }

      if (tag === 'supported-platform-and-api-in-awc-platform') {
        const singleWalletApiFunctionsChildrenSectionData = Object.values(props.sectionsBySectionId).filter(s => !isRootParentSectionId(s.parentSectionId) && s.tag === 'single-wallet-api-functions').sort((a, b) => a.sortOrder - b.sortOrder)

        const columnTitleOptions = singleWalletApiFunctionsChildrenSectionData.map(s => ({
          value: s.operationId,
          label: s.operationId,
        }))

        console.log('[AppendixSection] columnTitleOptions', columnTitleOptions)
        return {
          addibleColumn: {
            element: 'select',
            options: columnTitleOptions,
          },
          columns: sortedColumns.map(({
            columnId, columnTitle, sortOrder, ...column
          }) => ({
            title: columnTitle,
            dataIndex: columnId,
            key: columnId,
            ...column,
            ...(sortOrder === 0 && { fixed: 'left' }),
            ...(columnId === 'platform' ? {
              width: 200,
              options: platformOptions.value,
              showSearch: true,
              required: true,
              columnTitleOptions,
            } : {
              width: 150,
            }),
          })),
        }
      }

      if (tag === 'request-time-interval-limits') {
        const apiFunctionsChildrenSections = Object.values(props.sectionsBySectionId).filter((s) => {
          if (isRootParentSectionId(s.parentSectionId)) return false
          if (wallet === 'single') return s.tag === 'common-api-functions'
          if (wallet === 'multi') return s.tag === 'common-api-functions' || s.tag === 'multi-wallet-api-functions'
          return false
        }).sort((a, b) => {
          if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder
          return a.tag.localeCompare(b.tag)
        })
        console.log('[AppendixSection] apiFunctionsChildrenSections', apiFunctionsChildrenSections)
        return {
          addibleColumn: {
            element: 'textarea',
          },
          columns: sortedColumns.map(({
            columnId, columnTitle, sortOrder, ...column
          }) => ({
            title: columnTitle,
            dataIndex: columnId,
            key: columnId,
            ...column,
            ...(sortOrder === 0 && { fixed: 'left' }),
            ...(columnId === 'api' ? {
              width: 200,
              options: apiFunctionsChildrenSections.map(s => ({
                value: s.operationId,
                label: s.operationId,
              })),
              showSearch: true,
              required: true,
            } : {
              width: 300,
            }),
          })),
        }
      }

      if (['promotionType-id', 'parameter-combination-to-enter-specified-table', 'gameTable-id'].includes(tag)) {
        return {
          // addibleColumn: {
          //   element: 'textarea',
          // },
          columns: sortedColumns.map(({
            columnId, columnTitle, sortOrder, ...column
          }) => ({
            title: columnTitle,
            dataIndex: columnId,
            key: columnId,
            ...column,
            ...(sortOrder === 0 && { fixed: 'left' }),
            ...(columnId === 'platform' ? {
              width: 150,
              options: platformOptions.value,
              showSearch: true,
              required: true,
            } : {
              width: 200,
            }),
          })),
        }
      }

      if (tag === 'platforms-regional-ip-restriction') {
        return {
          columns: sortedColumns.map(({
            columnId, columnTitle, sortOrder, ...column
          }) => ({
            title: columnTitle,
            dataIndex: columnId,
            key: columnId,
            ...column,
            ...(sortOrder === 0 && { fixed: 'left' }),
            ...(columnId === 'platform' ? {
              width: 100,
              options: platformOptions.value,
              showSearch: true,
              required: true,
            } : {
              width: 200,
            }),
          })),
        }
      }

      if (tag === 'refPlatformTxId-value-definition') {
        return {
          columns: sortedColumns.map(({
            columnId, columnTitle, sortOrder, ...column
          }) => ({
            title: columnTitle,
            dataIndex: columnId,
            key: columnId,
            ...column,
            ...(sortOrder === 0 && { fixed: 'left' }),
            ...(columnId === 'platform' && {
              width: 100,
              options: platformOptions.value,
              showSearch: true,
              required: true,
            }),
            ...(columnId === 'column_1' && { // Game Types
              width: 200,
              options: GAMETYPE_OPTIONS,
              mode: 'multiple',
              showSearch: true,
              sortValue: true,
              required: true,
            }),
            ...(columnId === 'column_2' && { // Has Bet Limit Setting
              width: 150,
            }),
            ...(columnId === 'column_3' && { // Bet Limit Setting
              width: 300,
            }),
          })),
        }
      }

      return {}
    }

    const activePlatformIdxes = ref((selectedSectionState.tablePlatformsOrder || []).map((_, idx) => idx))
    const platformSelectOpenIdx = ref(null)

    const scrollToPlatformPanel = async (idx) => {
      await nextTick()
      const panel = appendixRootRef.value?.querySelector(`.platform-collapse-item[data-platform-idx="${idx}"]`)
      if (panel) {
        panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }

    const onPlatformSelectDropdownChange = (open, idx) => {
      if (open) {
        platformSelectOpenIdx.value = idx
      } else if (platformSelectOpenIdx.value === idx) {
        platformSelectOpenIdx.value = null
      }
    }

    const isVisiblePlatform = platform => platform == null || platformOptions.value.some(({ value }) => platform === value)

    const remapActivePlatformKeys = (keys, fromIdx, toIdx) => {
      if (fromIdx === toIdx) return keys
      return keys.map((key) => {
        const k = Number(key)
        if (Number.isNaN(k)) return key
        if (k === fromIdx) return toIdx
        if (fromIdx < toIdx && k > fromIdx && k <= toIdx) return k - 1
        if (fromIdx > toIdx && k >= toIdx && k < fromIdx) return k + 1
        return k
      })
    }

    const reorderTablePlatformsOrder = (fromIdx, toIdx) => {
      const order = selectedSectionState.tablePlatformsOrder || []
      const visibleIndices = []
      const visibleValues = []

      order.forEach((platform, fullIdx) => {
        if (isVisiblePlatform(platform)) {
          visibleIndices.push(fullIdx)
          visibleValues.push(platform)
        }
      })

      const reordered = [...visibleValues]
      const [moved] = reordered.splice(fromIdx, 1)
      reordered.splice(toIdx, 0, moved)

      const newOrder = [...order]
      visibleIndices.forEach((fullIdx, i) => {
        newOrder[fullIdx] = reordered[i]
      })
      selectedSectionState.tablePlatformsOrder.splice(0, newOrder.length, ...newOrder)
      activePlatformIdxes.value = remapActivePlatformKeys(activePlatformIdxes.value, fromIdx, toIdx)
    }

    const dragPlatformIdx = ref(null)

    const clearPlatformDragTarget = () => {
      const nodes = appendixRootRef.value?.querySelectorAll('.platform-collapse-item.target')
      if (nodes) {
        nodes.forEach(el => el.classList.remove('target'))
      }
    }

    const onPlatformDragstart = (e, idx) => {
      e.stopPropagation()
      dragPlatformIdx.value = idx
      e.dataTransfer.effectAllowed = 'move'
    }

    const onPlatformHeaderDragenter = (e) => {
      e.preventDefault()
      clearPlatformDragTarget()
      const target = e.currentTarget.closest('.platform-collapse-item')
      if (target) target.classList.add('target')
    }

    const onPlatformHeaderDrop = (e, toIdx) => {
      e.stopPropagation()
      clearPlatformDragTarget()
      const fromIdx = dragPlatformIdx.value
      dragPlatformIdx.value = null
      if (fromIdx == null || fromIdx === toIdx) return
      reorderTablePlatformsOrder(fromIdx, toIdx)
    }

    const ensureRowScope = () => {
      if (!selectedFieldState.row) selectedFieldState.row = {}
    }

    const expandPlatformPanel = (idx) => {
      if (!activePlatformIdxes.value.includes(idx)) {
        activePlatformIdxes.value = [...activePlatformIdxes.value, idx]
      }
    }

    const onAddPlatform = async () => {
      if (!selectedSectionState.tablePlatformsOrder) {
        selectedSectionState.tablePlatformsOrder = []
      }

      selectedSectionState.tablePlatformsOrder.push(undefined)
      const newIdx = selectedSectionState.tablePlatformsOrder.length - 1

      ensureRowScope()
      expandPlatformPanel(newIdx)
      platformSelectOpenIdx.value = newIdx
      await scrollToPlatformPanel(newIdx)
    }

    const onChangeTableDataPlatform = (idx, newPlatform) => {
      const oldPlatform = selectedSectionState.tablePlatformsOrder[idx]
      if (oldPlatform === newPlatform) return

      if (selectedSectionState.tablePlatformsOrder.includes(newPlatform)) {
        antMessage.error('Platform already exists')
        return
      }

      selectedSectionState.tablePlatformsOrder[idx] = newPlatform
      platformSelectOpenIdx.value = null
      ensureRowScope()
      expandPlatformPanel(idx)
    }

    const onDeletePlatform = (idx) => {
      const deletedPlatform = selectedSectionState.tablePlatformsOrder[idx]
      selectedSectionState.tablePlatformsOrder.splice(idx, 1)

      if (platformSelectOpenIdx.value === idx) {
        platformSelectOpenIdx.value = null
      } else if (platformSelectOpenIdx.value != null && platformSelectOpenIdx.value > idx) {
        platformSelectOpenIdx.value -= 1
      }

      if (deletedPlatform && selectedFieldState.row) {
        Object.values(selectedFieldState.row).forEach((row) => {
          if (row.platform === deletedPlatform) row.deleted = true
        })
      }

      activePlatformIdxes.value = activePlatformIdxes.value
        .filter(key => Number(key) !== idx)
        .map(key => (Number(key) > idx ? Number(key) - 1 : Number(key)))
    }

    const onChangeContent = (val) => {
      const docId = customizedVersionSelectedDocId.value
      if (docId) {
        if (!selectedSectionState.customizedVersion) selectedSectionState.customizedVersion = {}
        if (!selectedSectionState.customizedVersion[docId]) {
          selectedSectionState.customizedVersion[docId] = { docId }
        }
        selectedSectionState.customizedVersion[docId].content = val
      } else {
        selectedSectionState.content = val
      }
    }

    const getCellValidationErrorType = (record, column) => {
      if (!record || record.isNew || record.deleted || !column?.element) return null
      const value = record[column.dataIndex]
      if (column.required && (!value || !value?.length)) return 'required'
      if (column.pattern && !column.pattern.test(value)) return 'pattern'
      return null
    }

    const findFirstTableValidationError = () => {
      const columns = getTableDataTableProps().columns || []
      const rows = Object.values(selectedFieldState.row || {})
        .filter(row => !row.deleted)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))

      for (const record of rows) {
        for (const column of columns) {
          if (!column?.element || ['sort', 'action'].includes(column.dataIndex)) continue
          const errorType = getCellValidationErrorType(record, column)
          if (!errorType) continue
          return {
            fieldId: record.fieldId,
            dataIndex: column.dataIndex,
            errorType,
            platform: record.platform,
            record,
            column,
          }
        }
      }
      return null
    }

    const waitForTableRef = async (getRef, maxMs = 3000) => {
      const deadline = Date.now() + maxMs
      while (Date.now() < deadline) {
        const tableRef = getRef()
        if (tableRef?.focusTableCellValidationErrorInput) return tableRef
        await nextTick()
        await new Promise(resolve => requestAnimationFrame(resolve))
      }
      return getRef()
    }

    const focusTableValidationErrorIfNeeded = async () => {
      const err = findFirstTableValidationError()
      if (!err) return false

      activeAppendixTab.value = 'Data Table'
      await nextTick()
      await nextTick()

      if (filteredPlatformsOrder.value?.length) {
        const platformIdx = filteredPlatformsOrder.value.findIndex(platform => platform === err.platform)
        if (platformIdx >= 0) {
          const openedIdxes = activePlatformIdxes.value.map(key => Number(key))
          if (!openedIdxes.includes(platformIdx)) {
            activePlatformIdxes.value = [...activePlatformIdxes.value, platformIdx]
          }
          await nextTick()
          await new Promise(resolve => setTimeout(resolve, 150))
          const tableRef = await waitForTableRef(() => tableDataTableRefsByPlatform[err.platform])
          if (tableRef?.focusTableCellValidationErrorInput) {
            await tableRef.focusTableCellValidationErrorInput(err)
          }
          return true
        }
      }

      await new Promise(resolve => setTimeout(resolve, 150))
      const tableRef = await waitForTableRef(() => tableDataTableRef.value)
      if (tableRef?.focusTableCellValidationErrorInput) {
        await tableRef.focusTableCellValidationErrorInput(err)
      }
      return true
    }

    const refocusTableValidationErrorIfNeeded = async () => {
      if (filteredPlatformsOrder.value?.length) {
        const err = findFirstTableValidationError()
        if (!err?.platform) return false
        const tableRef = await waitForTableRef(() => tableDataTableRefsByPlatform[err.platform])
        if (tableRef?.refocusTableCellValidationErrorInput) {
          return tableRef.refocusTableCellValidationErrorInput()
        }
        return false
      }

      const tableRef = await waitForTableRef(() => tableDataTableRef.value)
      if (tableRef?.refocusTableCellValidationErrorInput) {
        return tableRef.refocusTableCellValidationErrorInput()
      }
      return false
    }

    expose({
      focusTableValidationErrorIfNeeded,
      refocusTableValidationErrorIfNeeded,
    })

    return {
      isDev,
      emit,
      appendixRootRef,
      tableDataTableRef,
      setPlatformTableRef,
      activeAppendixTab,
      activeAppendixDescType,
      isTableLoading,
      platformOptions, // eslint-disable-line vue/no-dupe-keys
      customizedVersionSelectedDocId,
      descriptionEditorContent,
      filteredPlatformsOrder,
      getTableDataTableProps,
      activePlatformIdxes,
      platformSelectOpenIdx,
      onPlatformSelectDropdownChange,
      onAddPlatform,
      onDeletePlatform,
      onChangeTableDataPlatform,
      onPlatformDragstart,
      onPlatformHeaderDragenter,
      onPlatformHeaderDrop,
      onChangeContent,
    }
  },
}
</script>

<style scoped>
.appendix-section :deep(.ant-collapse-header) {
  align-items: center;
}

.platform-collapse-header {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.platform-drag-handle {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.appendix-section :deep(.platform-collapse-item.target > .ant-collapse-header) {
  background-color: #e6f7ff;
}
</style>
